import { itemIncludesVat } from '@/lib/vat';
import type { ServiceInput } from '@/hooks/useServices';
import type {
  PractitionerEligibilityMode,
  ServiceResourceRequirementInput,
} from '@/hooks/useSchedulingResources';

export type ServiceEditorPaymentTiming = 'clinic_default' | 'upon_booking' | 'at_appointment';

export type ServiceEditorSummaryResource = {
  groupName: string;
  quantity: number;
  eligibleNames: string[];
  eligibilityMode: 'any' | 'selected';
};

export type ServiceEditorSummary = {
  name: string;
  durationMinutes: number;
  priceCents: number;
  currency: string;
  isActive: boolean;
  isBlocker: boolean;
  includesVat: boolean;
  paymentTiming: ServiceEditorPaymentTiming;
  practitionerMode: PractitionerEligibilityMode;
  practitionerNames: string[];
  resources: ServiceEditorSummaryResource[];
  questionnairesBefore: number;
  questionnairesAfter: number;
};

export type ServiceEditorSummaryInput = {
  form: Pick<
    ServiceInput,
    | 'name'
    | 'duration_minutes'
    | 'price_cents'
    | 'currency'
    | 'vat_rate_percent'
    | 'is_active'
    | 'is_blocker'
    | 'auto_send_questionnaires'
    | 'pre_visit_questionnaire_template_id'
    | 'questionnaire_automations'
    | 'payment_timing'
  >;
  practitionerEligibility: PractitionerEligibilityMode;
  selectedPractitionerIds: string[];
  practitionerNamesById: Record<string, string>;
  requirements: ServiceResourceRequirementInput[];
  resourceGroups: Array<{ id: string; name: string }>;
  resources: Array<{ id: string; name: string; resource_group_id: string }>;
};

export function parseServiceEditorPaymentTiming(
  value: ServiceInput['payment_timing'],
): ServiceEditorPaymentTiming {
  if (value === 'upon_booking' || value === 'at_appointment') return value;
  return 'clinic_default';
}

export function buildServiceEditorSummary(input: ServiceEditorSummaryInput): ServiceEditorSummary {
  const groupNameById = new Map(input.resourceGroups.map((group) => [group.id, group.name]));
  const resourcesByGroup = new Map<string, Array<{ id: string; name: string }>>();
  for (const resource of input.resources) {
    const list = resourcesByGroup.get(resource.resource_group_id) ?? [];
    list.push({ id: resource.id, name: resource.name });
    resourcesByGroup.set(resource.resource_group_id, list);
  }

  const resources: ServiceEditorSummaryResource[] = input.requirements
    .filter((req) => req.resource_group_id)
    .map((req) => {
      const groupResources = resourcesByGroup.get(req.resource_group_id) ?? [];
      const eligibilityMode = req.eligibility_mode === 'selected' ? 'selected' : 'any';
      const eligibleNames =
        eligibilityMode === 'selected'
          ? groupResources
              .filter((resource) => req.eligible_resource_ids.includes(resource.id))
              .map((resource) => resource.name)
          : groupResources.map((resource) => resource.name);
      switch (eligibilityMode) {
        case 'any':
        case 'selected':
          return {
            groupName: groupNameById.get(req.resource_group_id) ?? req.resource_group_id,
            quantity: req.quantity,
            eligibleNames,
            eligibilityMode,
          };
        default: {
          const _exhaustive: never = eligibilityMode;
          return _exhaustive;
        }
      }
    });

  const automations = input.form.questionnaire_automations;
  const afterIds = input.form.auto_send_questionnaires ?? [];
  const questionnairesBefore = automations
    ? automations.filter((row) => row.phase === 'before_appointment' && row.is_active).length
    : input.form.pre_visit_questionnaire_template_id
      ? 1
      : 0;
  const questionnairesAfter = automations
    ? automations.filter((row) => row.phase === 'after_appointment' && row.is_active).length
    : afterIds.length;

  return {
    name: input.form.name.trim(),
    durationMinutes: input.form.duration_minutes,
    priceCents: input.form.price_cents,
    currency: input.form.currency ?? 'DKK',
    isActive: input.form.is_active ?? true,
    isBlocker: Boolean(input.form.is_blocker),
    includesVat: itemIncludesVat(input.form.vat_rate_percent),
    paymentTiming: parseServiceEditorPaymentTiming(input.form.payment_timing),
    practitionerMode: input.practitionerEligibility,
    practitionerNames: input.selectedPractitionerIds
      .map((id) => input.practitionerNamesById[id])
      .filter((name): name is string => Boolean(name)),
    resources,
    questionnairesBefore,
    questionnairesAfter,
  };
}

export function formatServicePrice(cents: number, currency: string, locale: string): string {
  return (cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency,
  });
}

export function formatResourceRequirementSummary(resource: ServiceEditorSummaryResource): string {
  const quantityLine = `${resource.quantity} × ${resource.groupName}`;
  if (resource.eligibleNames.length === 0) return quantityLine;
  return `${quantityLine} (${resource.eligibleNames.join(', ')})`;
}

export function hasConfiguredQuestionnaires(summary: ServiceEditorSummary): boolean {
  return summary.questionnairesBefore > 0 || summary.questionnairesAfter > 0;
}

export function hasConfiguredBookingRequirements(summary: ServiceEditorSummary): boolean {
  return summary.practitionerMode === 'selected' || summary.resources.length > 0;
}
